class CartModal extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.close();
        return;
      }
      if (event.target.closest('.CartModal__close')) {
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (window.location.pathname !== '/cart') this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-modal-open');

    requestAnimationFrame(() => {
      const dialog = this.querySelector('.CartModal__dialog');
      const focusElement = this.querySelector('.CartModal__close') || dialog;
      if (typeof trapFocus === 'function') trapFocus(dialog, focusElement);
    });
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-modal-open');
  }

  renderContents(parsedState, byPassSliderCart = false, quietAdd = false) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (quietAdd) return;
      if (!byPassSliderCart) {
        this.open();
      } else {
        window.location = window.routes.cart_url;
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-modal', selector: '.CartModal__dialog' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-modal', CartModal);

class CartModalItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartModal',
        section: 'cart-modal',
        selector: '.CartModal__dialog',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-modal`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-modal-items', '.CartModal__card--addons'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define('cart-modal-items', CartModalItems);

class CartItemsScroller extends HTMLElement {
  // Overflow tolerance (px) below which the list is treated as fitting, so a
  // near-exact fit doesn't show a phantom chevron / 1px scrollbar.
  static OVERFLOW_TOLERANCE = 4;

  constructor() {
    super();
    this.handleScrollBound = this.handleScroll.bind(this);
    this.scrollFrame = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.scrollRegion = this.querySelector('.CartModal__items');
    if (!this.scrollRegion) return;

    this.measureRowHeight();
    this.updateScrollState();

    this.scrollRegion.addEventListener('scroll', this.handleScrollBound, { passive: true });

    // The modal mounts hidden (display: none), so the first measurement reads
    // zero height and the fade/chevron never appear. Re-measure whenever the
    // region gains or changes size — chiefly when the modal opens and the list
    // first becomes visible.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measureRowHeight();
        this.updateScrollState();
      });
      this.resizeObserver.observe(this.scrollRegion);
    }
  }

  disconnectedCallback() {
    if (this.scrollRegion) {
      this.scrollRegion.removeEventListener('scroll', this.handleScrollBound);
    }
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  measureRowHeight() {
    const firstRow = this.scrollRegion.querySelector('.cart-item');
    if (!firstRow) return;

    const rowHeight = firstRow.getBoundingClientRect().height;
    if (rowHeight > 0) {
      this.style.setProperty('--cart-items-max-row-height', `${Math.round(rowHeight)}px`);
    }
  }

  updateScrollState() {
    const scrollableDistance = this.scrollRegion.scrollHeight - this.scrollRegion.clientHeight;
    const isScrollable = scrollableDistance > CartItemsScroller.OVERFLOW_TOLERANCE;
    this.classList.toggle('is-scrollable', isScrollable);

    const isAtBottom =
      scrollableDistance - this.scrollRegion.scrollTop <= CartItemsScroller.OVERFLOW_TOLERANCE;
    this.classList.toggle('is-at-bottom', isAtBottom);
  }

  handleScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateScrollState();
    });
  }
}

customElements.define('cart-items-scroller', CartItemsScroller);
